import { Card } from '@mui/material';
import { useState } from 'react';
import { NotesDisplayWithAdd, CRS_NOTES_COLUMNS } from 'src/components/NotesDisplay';
import { DocumentList } from '../attachments/DocumentList';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { useCommunications, useDocumentReferences } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { CommunicationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Communication';
import { mapCommunicationsToNotesDisplay } from 'src/careflow/common/common-utils';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import ExpandableCard from 'src/components/common/ExpandableCard';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { AssessmentList } from 'src/careflow/care-coordination/patient/assessment/list/AssessmentList';
import NoteAddForm from 'src/careflow/common/NoteAddForm';

type Props = {
  patient: WrappedPatient | null;
};

export default function PatientDocumentSummary({ patient }: Props) {
  const [communications, { create: createCommunication, refresh: refreshCommunications }] =
    useCommunications({ filter: { subject: patient?.id }, map: CommunicationWrapper });
  const [documentReferences, { refresh: refreshDocumentReference }] = useDocumentReferences({
    filter: { subject: patient?.id, _sort: '-date' },
  });

  const [openNote, setOpenNote] = useState(false);
  const { i18n } = useLocales();

  const handleCloseAddNote = () => {
    setOpenNote(false);
  };

  const handleCreateCommunicationFhir = async (data: any) => {
    let result: any = {};
    try {
      result = await createCommunication(data);
      handleCloseAddNote();
      refreshCommunications();
    } catch (err) {
      console.log('error on handleCreateCommunicationFhir', err);
    }

    return result;
  };

  return (
    <Card sx={{ py: 2, boxShadow: 'none' }}>
      {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.ASSESSMENTS.VIEW] }) && (
        <ExpandableCard title={i18n('patients.details.assessments.title', 'crs')} sxProp={{ m: 1 }}>
          <AssessmentList patient={patient} i18n={i18n} />
        </ExpandableCard>
      )}
      {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.ATTACHMENTS.VIEW] }) && (
        <ExpandableCard title={i18n('patients.details.attachments.title', 'crs')} sxProp={{ m: 1 }}>
          <Card style={{ boxShadow: 'none', position: 'static' }} sx={{ m: 1 }}>
            <DocumentList
              patient={patient}
              documentReferences={documentReferences}
              showFilters={true}
              refreshDocumentReference={refreshDocumentReference}
              openCollapseExternal={true}
              handleUpdateResource={null}
              permissions={{
                isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.ATTACHMENTS.ADD] }),
                isAllowedToEdit: checkAclValidation({
                  acls: [crsAcls.CRS.PATIENT.ATTACHMENTS.EDIT],
                }),
              }}
            />
          </Card>
        </ExpandableCard>
      )}

      {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.NOTES.VIEW] }) && (
        <ExpandableCard title={i18n('patients.details.notes.title', 'crs')} sxProp={{ m: 1 }}>
          <NotesDisplayWithAdd
            data={mapCommunicationsToNotesDisplay(communications, [])}
            columns={CRS_NOTES_COLUMNS}
            titleButton={
              checkAclValidation({ acls: [crsAcls.CRS.PATIENT.NOTES.ADD] })
                ? i18n('patients.details.notes.button', 'crs')
                : undefined
            }
            isAllowedToAdd={checkAclValidation({ acls: [crsAcls.CRS.PATIENT.NOTES.ADD] })}
            onAddNote={() => setOpenNote(true)}
            emptyMessage="No patient notes available"
          />
        </ExpandableCard>
      )}

      <NoteAddForm
        open={openNote}
        patient={patient}
        resource={[]}
        onCancel={handleCloseAddNote}
        handleCreate={handleCreateCommunicationFhir}
        typeNote="notes_patient"
      />
    </Card>
  );
}
